import { ExclamationCircleIcon } from '@heroicons/react/outline'
import { UseFormReturnExt } from '@types'
import clsx from 'clsx'
import ResetButton from 'components/forms/ResetButton'
import { OnSubmitResult } from 'hooks/useBaseForm'
import {
  addPropIf,
  isEmptyString,
  isNonEmptyString,
  isPresent,
  isSame,
} from 'lib/utils'
import {
  KeyboardEvent,
  PropsWithoutRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form'

export interface Props
  extends PropsWithoutRef<JSX.IntrinsicElements['input']> {
  onEnterDown?: (...args: any) => Promise<void | OnSubmitResult>
  name: string
  className?: string
  withResetButton?: boolean
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'date'
  wrapperClasses?: string
  initFocus?: boolean
  absoluteError?: boolean
  isLoading?: boolean
  disabled?: boolean
  extError?: string
  placeholder?: string
}

let TextInputWithLabel = (props: Props) => {
  let {
    onEnterDown,
    name,
    type = 'text',
    className,
    initFocus,
    absoluteError = false,
    wrapperClasses = 'relative',
    withResetButton = true,
    isLoading = false,
    extError,
    placeholder,
    disabled = false,
    ...rest
  } = props

  let [focused, setFocused] = useState(false)

  let {
    setValue,
    register,
    setFocus,
    formState: {
      isSubmitting,
      errors: { [name]: inputErrors },
    },
    handleSubmit,
  } = useFormContext() as UseFormReturnExt<FieldValues>

  useEffect(() => {
    initFocus && setFocus(name)
  }, [initFocus, name, setFocus])

  let value = useWatch({ name })
  let { ref, onBlur, ...registerRest } = register(name)

  let inputRef = useRef<HTMLInputElement | null>(null)
  let handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (isSame(e.key, 'Escape')) {
        e.preventDefault()
        inputRef.current!.blur()
      }

      if (isSame(e.key, 'Enter')) {
        e.preventDefault()
        inputRef.current!.blur()
        isPresent(onEnterDown) && void handleSubmit(onEnterDown)()
      }
    },
    [handleSubmit, onEnterDown]
  )

  let error = useMemo(
    () =>
      Array.isArray(inputErrors)
        ? inputErrors[0]
        : inputErrors?.message || inputErrors,
    [inputErrors]
  )

  let rootClassName = clsx(className, {
    'form-error':
      isNonEmptyString(error) || isNonEmptyString(extError),
    'form-disabled': isSubmitting || isLoading,
  })

  let resetValue = useCallback(() => {
    setValue(name, '')
  }, [name, setValue])

  return (
    <>
      <div className={clsx(wrapperClasses)}>
        <input
          id={name}
          className={clsx(
            'h-12 rounded-md form-input w-full placeholder-transparent pl-5 pr-12 peer transition-spacing',
            rootClassName,
            {
              'pt-4 pb-1': isNonEmptyString(value) || focused,
              'py-2.5':
                (isEmptyString(value) && !focused) || type === 'date',
            }
          )}
          readOnly={isSubmitting || isLoading}
          type={type}
          {...addPropIf(
            isSame(type, 'number'),
            'inputMode',
            'decimal'
          )}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false)
            void onBlur(e)
          }}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          ref={(e: HTMLInputElement | null) => {
            ref(e)
            inputRef.current = e
          }}
          disabled={disabled}
          {...registerRest}
          {...rest}
        />

        <label
          htmlFor={name}
          className={clsx(
            'truncate right-9 absolute text-2xs top-1 left-5.5 -ml-px -mt-px peer-placeholder-shown:left-5 peer-placeholder-shown:right-5 peer-placeholder-shown:mt-px peer-placeholder-shown:ml-px peer-placeholder-shown:text-base peer-placeholder-shown:leading-7 peer-placeholder-shown:top-2.5 transition-all peer-focus:top-1 peer-focus:left-5.5 peer-focus:text-2xs peer-focus:-mt-px peer-focus:-ml-px peer-focus:right-9 pointer-events-none',
            {
              'text-bodyGrey cursor-not-allowed peer-placeholder-shown:text-bodyGrey peer-focus:text-bodyGrey peer-placeholder-shown:cursor-not-allowed peer-focus:cursor-not-allowed':
                isSubmitting || isLoading || disabled,
              'text-bodyGrey cursor-text':
                !isSubmitting && !isLoading,
            }
          )}
        >
          {placeholder}
        </label>

        {withResetButton && !disabled && (
          <ResetButton
            condition={
              !isSubmitting && !isLoading && isNonEmptyString(value)
            }
            onClick={resetValue}
            className='right-4 top-3.5'
          />
        )}
      </div>

      {(isNonEmptyString(error) || isNonEmptyString(extError)) && (
        <div
          className={clsx('mb-3.5 flex items-start lg:mt-1 mt-1.5', {
            absolute: absoluteError,
          })}
        >
          <ExclamationCircleIcon className='flex-shrink-0 w-4 h-4 text-warning' />
          <div className='ml-1 text-xs leading-tight text-warning'>
            {error ?? extError}
          </div>
        </div>
      )}
    </>
  )
}

export default TextInputWithLabel
